// User Management Service
// API service for user management operations

import { AxiosResponse } from 'axios';
import sharedApiClient from './apiClient';
import permissionApiAdapter from '../api/permissionApiAdapter';
import type {
  User,
  UserListParams,
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserStatusRequest,
  UserPasswordRequest,
  UserPositionsRequest,
  UserPermissionsRequest,
  Position,
  PermissionGroup,
  PermissionGroupCreateRequest,
  PermissionGroupUpdateRequest,
  Program,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  RefreshTokenRequest,
  UserStatistics,
  UserSession,
  LoginAttempt,
  OnlineUserMetrics,
  ApiResponse,
  SuccessResponse
} from '../types/user';

// UserService uses /api/v1 prefix for all its endpoints
const V1_PREFIX = '/api/v1';
const apiClient = sharedApiClient;

// setAuthToken is no longer needed — the shared apiClient interceptor handles it.
// Kept as a no-op for backward compatibility with AuthContext imports.
export const setAuthToken = (_token: string | null) => {};

export class UserService {
  // Authentication APIs
  
  static async login(data: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<SuccessResponse<LoginResponse>> = await apiClient.post(`${V1_PREFIX}/auth/login`, data);
    return response.data.data;
  }

  static async logout(): Promise<void> {
    await apiClient.post(`${V1_PREFIX}/auth/logout`);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('refreshToken');
  }

  static async refreshToken(data: RefreshTokenRequest): Promise<LoginResponse> {
    const response: AxiosResponse<SuccessResponse<LoginResponse>> = await apiClient.post(`${V1_PREFIX}/auth/refresh`, data);
    return response.data.data;
  }

  static async changePassword(data: ChangePasswordRequest): Promise<void> {
    await apiClient.post(`${V1_PREFIX}/auth/password/change`, data);
  }

  static async getProfile(): Promise<User> {
    const response: AxiosResponse<SuccessResponse<User>> = await apiClient.get(`${V1_PREFIX}/auth/profile`);
    return response.data.data;
  }

  static async validateToken(): Promise<boolean> {
    try {
      await apiClient.get(`${V1_PREFIX}/auth/validate`);
      return true;
    } catch {
      return false;
    }
  }

  static async unlockUser(userId: string): Promise<void> {
    await apiClient.post(`${V1_PREFIX}/auth/unlock`, { userId });
  }

  // User Management APIs

  static async listUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const response: AxiosResponse<SuccessResponse<UserListResponse>> = await apiClient.get(`${V1_PREFIX}/users`, { params });
    return response.data.data;
  }

  static async getUser(userId: string): Promise<User> {
    const response: AxiosResponse<SuccessResponse<User>> = await apiClient.get(`${V1_PREFIX}/users/${userId}`);
    return response.data.data;
  }

  static async createUser(data: UserCreateRequest): Promise<User> {
    const response: AxiosResponse<SuccessResponse<User>> = await apiClient.post(`${V1_PREFIX}/users`, data);
    return response.data.data;
  }

  static async updateUser(userId: string, data: UserUpdateRequest): Promise<User> {
    const response: AxiosResponse<SuccessResponse<User>> = await apiClient.put(`${V1_PREFIX}/users/${userId}`, data);
    return response.data.data;
  }

  static async deleteUser(userId: string): Promise<void> {
    console.log(`[UserService] Attempting to delete user: ${userId}`);
    const response = await apiClient.delete(`${V1_PREFIX}/users/${userId}`);
    console.log(`[UserService] Delete response:`, response);
  }

  static async updateUserStatus(userId: string, data: UserStatusRequest): Promise<User> {
    const response: AxiosResponse<SuccessResponse<User>> = await apiClient.patch(`${V1_PREFIX}/users/${userId}/status`, data);
    return response.data.data;
  }

  static async resetUserPassword(userId: string, data: UserPasswordRequest): Promise<void> {
    await apiClient.patch(`${V1_PREFIX}/users/${userId}/password`, data);
  }

  // User Position APIs

  static async getUserPositions(userId: string): Promise<Position[]> {
    const response: AxiosResponse<SuccessResponse<Position[]>> = await apiClient.get(`${V1_PREFIX}/users/${userId}/positions`);
    return response.data.data;
  }

  static async setUserPositions(userId: string, data: UserPositionsRequest): Promise<Position[]> {
    const response: AxiosResponse<SuccessResponse<Position[]>> = await apiClient.put(`${V1_PREFIX}/users/${userId}/positions`, data);
    return response.data.data;
  }

  static async addUserPosition(userId: string, positionId: number, isMain: boolean = false): Promise<void> {
    await apiClient.post(`${V1_PREFIX}/users/${userId}/positions`, { positionId, isMain });
  }

  static async removeUserPosition(userId: string, positionId: number): Promise<void> {
    await apiClient.delete(`${V1_PREFIX}/users/${userId}/positions/${positionId}`);
  }

  // User Permission APIs

  static async getUserPermissions(userId: string): Promise<any> {
    const response = await permissionApiAdapter.getUserPermissions(userId);
    return response.data;
  }

  static async setUserPermissions(userId: string, data: UserPermissionsRequest): Promise<any> {
    const response = await permissionApiAdapter.setUserPermissions(userId, data);
    return response.data;
  }

  static async getUserEffectivePermissions(userId: string): Promise<any> {
    const response = await permissionApiAdapter.getUserEffectivePermissions(userId);
    return response.data;
  }

  static async getCurrentUserPermissions(): Promise<any> {
    const response = await permissionApiAdapter.getCurrentUserPermissions();
    return response.data;
  }

  // Permission Group APIs

  static async listPermissionGroups(params: { page?: number; limit?: number; search?: string; isActive?: boolean } = {}): Promise<{ items: PermissionGroup[]; pagination: any }> {
    const response = await permissionApiAdapter.getPermissionGroups(params);
    return response.data;
  }

  static async getPermissionGroup(rGroupId: string): Promise<PermissionGroup> {
    const response = await permissionApiAdapter.getPermissionGroup(rGroupId);
    return response.data;
  }

  static async createPermissionGroup(data: PermissionGroupCreateRequest): Promise<PermissionGroup> {
    const response = await permissionApiAdapter.createPermissionGroup(data);
    return response.data;
  }

  static async updatePermissionGroup(rGroupId: string, data: PermissionGroupUpdateRequest): Promise<PermissionGroup> {
    const response = await permissionApiAdapter.updatePermissionGroup(rGroupId, data);
    return response.data;
  }

  static async deletePermissionGroup(rGroupId: string): Promise<void> {
    await permissionApiAdapter.deletePermissionGroup(rGroupId);
  }

  // Group Members APIs
  
  static async getGroupMembers(groupId: string, params?: { page?: number; limit?: number; search?: string }): Promise<any> {
    const response = await permissionApiAdapter.getGroupMembers(groupId, params);
    return response.data;
  }

  static async addGroupMembers(groupId: string, userIds: string[]): Promise<void> {
    await permissionApiAdapter.addGroupMembers(groupId, userIds);
  }

  static async removeGroupMembers(groupId: string, userIds: string[]): Promise<void> {
    await permissionApiAdapter.removeGroupMembers(groupId, userIds);
  }

  // Group Permissions APIs
  
  static async getGroupPermissions(groupId: string): Promise<any> {
    const response = await permissionApiAdapter.getGroupPermissions(groupId);
    return response.data;
  }

  static async setGroupPermissions(groupId: string, data: UserPermissionsRequest): Promise<void> {
    await permissionApiAdapter.setGroupPermissions(groupId, data);
  }

  static async updateGroupPermissions(groupId: string, permissions: any[]): Promise<void> {
    const payload = {
      permissions: permissions.map(p => ({
        pgId: p.programId,
        query: p.query || false,
        addNew: p.addNew || false,
        edit: p.edit || false,
        del: p.delete || false,
        export: p.export || false,
        printer: p.print || false,
        approve: p.approve || false
      }))
    };

    const response = await apiClient.put(`/api/v2/permission-groups/${groupId}/permissions`, payload);
    return response.data;
  }

  // Program APIs

  static async listPrograms(active: boolean = true): Promise<Program[]> {
    // 載入所有程式
    const response = await permissionApiAdapter.getPrograms({
      active,
      limit: 500
    });
    console.log('[UserService] Programs loaded:', response.data.items);
    return response.data.items;
  }

  static async getProgramTree(): Promise<any[]> {
    const response = await permissionApiAdapter.getProgramTree();
    return response.data;
  }

  // Permission Check APIs
  
  static async checkPermission(userId: string, programId: number, permission: string): Promise<boolean> {
    const response = await permissionApiAdapter.checkPermission(userId, programId, permission);
    return response.data.hasPermission;
  }

  static async checkMultiplePermissions(userId: string, checks: Array<{ programId: number; permission: string }>): Promise<any> {
    const response = await permissionApiAdapter.checkMultiplePermissions({ userId, checks });
    return response.data.results;
  }

  // Statistics APIs

  static async getUserStatistics(): Promise<UserStatistics> {
    const response: AxiosResponse<SuccessResponse<UserStatistics>> = await apiClient.get(`${V1_PREFIX}/admin/system/stats`);
    return response.data.data;
  }

  // User Monitoring APIs

  static async getActiveSessions(params: { page?: number; limit?: number; userId?: string } = {}): Promise<{ items: UserSession[]; pagination: any }> {
    try {
      const response: AxiosResponse<SuccessResponse<{ items: UserSession[]; pagination: any }>> = await apiClient.get(`${V1_PREFIX}/auth/sessions`, { params });
      return response.data.data;
    } catch (error: any) {
      // 如果 API 尚未實作，返回模擬數據
      if (error.response?.status === 404) {
        return {
          items: [],
          pagination: {
            page: params.page || 1,
            limit: params.limit || 20,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
          }
        };
      }
      throw error;
    }
  }

  static async terminateSession(sessionId: string): Promise<void> {
    await apiClient.delete(`${V1_PREFIX}/auth/sessions/${sessionId}`);
  }

  static async terminateUserSessions(userId: string): Promise<void> {
    await apiClient.delete(`${V1_PREFIX}/auth/sessions/user/${userId}`);
  }

  static async getLoginAttempts(params: { page?: number; limit?: number; userId?: string; result?: string; startDate?: string; endDate?: string } = {}): Promise<{ items: LoginAttempt[]; pagination: any }> {
    try {
      const response: AxiosResponse<SuccessResponse<{ items: LoginAttempt[]; pagination: any }>> = await apiClient.get(`${V1_PREFIX}/auth/login-attempts`, { params });
      return response.data.data;
    } catch (error: any) {
      // 如果 API 尚未實作，返回模擬數據
      if (error.response?.status === 404) {
        return {
          items: [],
          pagination: {
            page: params.page || 1,
            limit: params.limit || 20,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
          }
        };
      }
      throw error;
    }
  }

  static async getOnlineUserMetrics(): Promise<OnlineUserMetrics> {
    try {
      const response: AxiosResponse<SuccessResponse<OnlineUserMetrics>> = await apiClient.get(`${V1_PREFIX}/auth/online-metrics`);
      return response.data.data;
    } catch (error: any) {
      // 如果 API 尚未實作，返回模擬數據
      if (error.response?.status === 404) {
        return {
          totalOnline: 0,
          activeSessions: 0,
          newLoginsToday: 0,
          averageSessionTime: 0,
          topDepartments: [],
          sessionsByHour: []
        };
      }
      throw error;
    }
  }

  static async forceLogoutUser(userId: string, reason: string): Promise<void> {
    await apiClient.post(`${V1_PREFIX}/auth/force-logout`, { userId, reason });
  }

  static async getSessionDetails(sessionId: string): Promise<UserSession> {
    const response: AxiosResponse<SuccessResponse<UserSession>> = await apiClient.get(`/auth/sessions/${sessionId}`);
    return response.data.data;
  }

  // Utility Functions

  static formatUserId(userId: string): string {
    return userId ? userId.toUpperCase() : '';
  }

  static validatePassword(password: string): { isValid: boolean; message: string } {
    if (!password) {
      return { isValid: false, message: '密碼不能為空' };
    }
    if (password.length < 8) {
      return { isValid: false, message: '密碼長度至少8位' };
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return { isValid: false, message: '密碼必須包含大小寫字母和數字' };
    }
    return { isValid: true, message: '' };
  }

  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static formatProgramTree(programs: Program[]): Program[] {
    const programMap = new Map<number, Program>();
    const rootPrograms: Program[] = [];

    // Create map of all programs
    programs.forEach(program => {
      programMap.set(program.pgId, { ...program, children: [] });
    });

    // Build tree structure
    programs.forEach(program => {
      const currentProgram = programMap.get(program.pgId)!;
      if (program.pgParent && programMap.has(program.pgParent)) {
        const parent = programMap.get(program.pgParent)!;
        parent.children = parent.children || [];
        parent.children.push(currentProgram);
      } else {
        rootPrograms.push(currentProgram);
      }
    });

    return rootPrograms;
  }

  static getStatusDisplay(isActive: boolean): { text: string; color: string } {
    return isActive 
      ? { text: '啟用', color: 'green' }
      : { text: '停用', color: 'red' };
  }
}

// Export utility functions for use in components
export const userUtils = {
  formatUserId: UserService.formatUserId,
  validatePassword: UserService.validatePassword,
  validateEmail: UserService.validateEmail,
  formatProgramTree: UserService.formatProgramTree,
  getStatusDisplay: UserService.getStatusDisplay,

  getDefaultUserFormData: (): Partial<User> => ({
    isActive: true,
  }),
};

export default UserService;
